/**
 * The shared record of what was said, and when.
 *
 * Append-only: nobody edits what a colleague wrote. A promise is settled by
 * adding the entry that settles it, pointing back with `resolvesId`, so the
 * whole history stays legible.
 */
import type { MemberId } from './tenant.js';
import { calendarDay } from './format.js';

/**
 * What kind of thing happened. The list is deliberately short — someone working
 * through a call list will not pick from twenty options, and anything that does
 * not fit is a plain note.
 */
export type ActivityKind =
    | 'note'        // anything typed freehand
    | 'no_answer'   // rang out
    | 'declined'    // they cut the call
    | 'promise'     // committed to pay an amount by a date
    | 'payment'     // money actually arrived
    | 'visit'       // someone went in person
    | 'dispute'     // they are contesting the amount
    | 'system';     // written by the app, not a person

export const ACTIVITY_LABELS: Record<ActivityKind, string> = {
    note: 'Note',
    no_answer: 'No answer',
    declined: 'Call declined',
    promise: 'Promised to pay',
    payment: 'Payment received',
    visit: 'Visited',
    dispute: 'Disputed',
    // A change to the account — its rank, collector, contacts or date — made
    // by the person named beside it and recorded for them (0033).
    system: 'Account change',
};

/**
 * The promises in a thread nobody has answered yet, newest first — the ones a
 * person can mark kept or not kept.
 */
export function openPromises(thread: readonly ActivityEntry[]): ActivityEntry[] {
    const answered = new Set(thread.map(e => e.resolvesId).filter((id): id is string => Boolean(id)));
    return thread.filter(e => e.kind === 'promise' && !answered.has(e.id));
}

export interface ActivityEntry {
    id: string;
    customerId: string;
    authorMemberId?: MemberId;
    /** Kept beside the id so a departed colleague's entries still say who wrote them. */
    authorName: string;
    kind: ActivityKind;
    body: string;
    /** Set on a 'promise': what they committed to, and by when. */
    promisedAmount?: number;
    promisedOn?: string;
    /** On a settling entry, the id of the promise it answers. */
    resolvesId?: string;
    createdAt: string;
}

/** A promise, once we know how it turned out. */
export type PromiseState = 'open' | 'due' | 'overdue' | 'kept' | 'broken';

/**
 * How a promise stands today.
 *
 * A promise answered by a payment was kept; answered by anything else it was
 * not. Until something answers it, it is open, and it becomes overdue the day
 * after the date they gave — which is the moment somebody should be ringing.
 */
export function promiseState(
    entry: Pick<ActivityEntry, 'promisedOn'>,
    resolvedBy: Pick<ActivityEntry, 'kind'> | undefined,
    today: Date = new Date(),
): PromiseState {
    if (resolvedBy) return resolvedBy.kind === 'payment' ? 'kept' : 'broken';
    if (!entry.promisedOn) return 'open';

    // A bare day is a calendar day, not UTC midnight (see `calendarDay()`); a
    // full timestamp is a moment, and its local day is the one it fell on.
    const due = calendarDay(entry.promisedOn) ?? new Date(entry.promisedOn);
    if (isNaN(due.getTime())) return 'open';
    due.setHours(0, 0, 0, 0);
    const t = new Date(today);
    t.setHours(0, 0, 0, 0);

    if (due.getTime() < t.getTime()) return 'overdue';
    if (due.getTime() === t.getTime()) return 'due';
    return 'open';
}
